import React from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, Loader2, Lock, ArrowLeft, LogIn, LogOut } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useSeoEngine } from "../hooks/useSeoEngine";
import { Seo } from "../components/Seo";
import { AuthModal } from "../components/seo/AuthModal";
import { ReportView } from "../components/seo/ReportView";
import { PlanChooser } from "../components/seo/PlanChooser";
import { AuditHistory } from "../components/seo/AuditHistory";

const UserMenu = ({ user, onLogin, onLogout }) => {
  if (!user) {
    return (
      <Button onClick={onLogin} variant="outline" size="sm" className="rounded-lg border-slate-300" data-testid="login-button">
        <LogIn className="h-4 w-4 mr-1.5" /> Log in / Sign up
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-3" data-testid="user-menu">
      {user.picture ? (
        <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-bold">
          {(user.name || user.email || "U").charAt(0)}
        </span>
      )}
      <span className="hidden sm:block text-sm font-medium text-slate-700">{user.name || user.email}</span>
      <Button onClick={onLogout} variant="ghost" size="sm" className="rounded-lg text-slate-500 hover:text-slate-900" data-testid="logout-button">
        <LogOut className="h-4 w-4 mr-1.5" /> Logout
      </Button>
    </div>
  );
};

const LoginGate = ({ onLogin }) => (
  <section className="pb-24">
    <div className="container-wide">
      <div className="max-w-xl mx-auto rounded-2xl bg-white border border-slate-100 shadow-sm p-10 text-center" data-testid="login-gate">
        <span className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Lock className="h-7 w-7" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Your report is ready</h3>
        <p className="mt-2 text-slate-500">
          Log in or create a free account to view your SEO audit results, unlock the full report and save it to your account.
        </p>
        <Button onClick={onLogin} className="mt-6 h-12 px-7 rounded-xl bg-primary hover:bg-primary/90 text-base" data-testid="login-gate-button">
          <LogIn className="h-5 w-5 mr-2" /> Log in / Sign up to view
        </Button>
        <p className="mt-3 text-xs text-slate-400">Free to view — you only pay to unlock the full optimisation plan.</p>
      </div>
    </div>
  </section>
);

const LoadingSkeleton = () => (
  <section className="pb-24">
    <div className="container-wide">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-10 flex flex-col items-center text-center">
        <Loader2 className="h-10 w-10 text-primary animate-spin" />
        <p className="mt-4 font-display font-bold text-slate-900">Crawling &amp; analysing the site…</p>
        <p className="mt-1 text-sm text-slate-500">Our AI is inspecting titles, meta tags, headings, keywords and more.</p>
      </div>
    </div>
  </section>
);

const VerifyingOverlay = () => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
    <div className="rounded-2xl bg-white p-8 shadow-2xl flex flex-col items-center">
      <Loader2 className="h-8 w-8 text-primary animate-spin" />
      <p className="mt-3 font-semibold text-slate-900">Verifying your payment…</p>
    </div>
  </div>
);

const EngineHero = ({ engine, onOpenAuth }) => (
  <section className="relative pt-32 pb-14 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
    <div className="absolute inset-0 grid-pattern opacity-60" />
    <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
    <div className="container-wide relative">
      <div className="flex items-center justify-between mb-5">
        <Link to="/seo" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> Back to SEO
        </Link>
        <UserMenu user={engine.user} onLogin={onOpenAuth} onLogout={engine.logout} />
      </div>
      <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold text-primary">
        <Sparkles className="h-3.5 w-3.5" /> AI-Powered SEO Auto Engine
      </span>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.08] max-w-3xl text-balance">
        Automatically audit &amp; optimise any website
      </h1>
      <p className="mt-4 text-lg text-slate-500 max-w-2xl leading-relaxed">
        Enter a URL and our engine crawls the site, scores your on-page SEO, researches keywords,
        and generates a prioritised, AI-driven optimisation plan.
      </p>

      <form onSubmit={engine.handleAnalyze} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            value={engine.url}
            onChange={(e) => engine.setUrl(e.target.value)}
            placeholder="yourwebsite.com"
            className="h-12 pl-11 rounded-xl text-base"
            data-testid="seo-url-input"
          />
        </div>
        <Button type="submit" disabled={engine.loading} className="h-12 px-7 rounded-xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 text-base" data-testid="run-audit-button">
          {engine.loading ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analyzing…</>) : (<><Sparkles className="h-5 w-5 mr-2" /> Run Audit</>)}
        </Button>
      </form>
      <p className="mt-3 text-xs text-slate-400">Crawls the entry page plus a few internal links. Analysis takes ~15–30 seconds.</p>
    </div>
  </section>
);

const EngineBody = ({ engine, onOpenAuth }) => {
  const { user, loading, report, verifying, needLogin, showPlans, setShowPlans } = engine;
  return (
    <>
      {needLogin && !user && !loading && <LoginGate onLogin={onOpenAuth} />}

      {loading && <LoadingSkeleton />}

      {verifying && <VerifyingOverlay />}

      {report && !loading && (
        <ReportView
          report={report}
          paying={engine.paying}
          onUnlock={engine.handleUnlock}
          onDownload={engine.handleDownload}
          onShowPlans={() => setShowPlans(true)}
        />
      )}

      {showPlans && report && !report.is_paid && (
        <PlanChooser
          plans={report.plans || []}
          onSelect={engine.handleUnlock}
          onClose={() => setShowPlans(false)}
          paying={engine.paying}
        />
      )}

      {user && engine.historyItems.length > 0 && !loading && (
        <AuditHistory items={engine.historyItems} onOpen={engine.openHistoryReport} />
      )}
    </>
  );
};

const EngineInfoSection = () => (
  <section className="py-20 bg-white border-t border-slate-100" data-testid="engine-info-section">
    <div className="container-wide grid gap-12 lg:grid-cols-2">
      <div>
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">How It Works</span>
        <h2 className="font-display text-3xl font-extrabold text-slate-900 text-balance">How the SEO Auto Engine audits your website</h2>
        <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
          <p>
            The SEO Auto Engine is a free AI-powered SEO audit tool that analyses any website in seconds. Enter your URL
            and our crawler visits your entry page plus several internal pages, extracting the on-page signals search
            engines care about most: title tags, meta descriptions, heading structure, image alt text, word counts,
            internal and external links, canonical tags and mobile viewport configuration.
          </p>
          <p>
            That raw data is then analysed by a large language model trained on modern SEO best practices. Within about
            thirty seconds you receive an overall SEO health score out of 100, a letter grade, and a plain-English
            summary of how well your website is optimised for Google, Bing and other search engines — completely free,
            with no obligation.
          </p>
        </div>
      </div>
      <div className="space-y-4 text-slate-600 leading-relaxed lg:pt-24">
        <p>
          The full report goes much deeper. Unlocking it reveals every audit finding across your crawled pages, a
          researched list of keyword opportunities grouped by search intent and difficulty, AI-written title tags and
          meta descriptions ready to paste into your site, content suggestions that target what your customers actually
          search for, and a prioritised fix list ordered by SEO impact.
        </p>
        <p>
          Full access is a one-time payment of $19.99 AUD. You keep the unlocked report forever, can download it as a
          shareable HTML document, and every new audit you run for the next 30 days is automatically unlocked too — ideal
          for checking competitors or tracking your improvements. Prefer an expert to handle it? Our{" "}
          <Link to="/seo" className="font-semibold text-primary hover:underline">SEO services team</Link> can implement
          every recommendation for you.
        </p>
      </div>
    </div>
  </section>
);

const SeoAutoEngine = () => {
  const engine = useSeoEngine();
  const [showAuth, setShowAuth] = React.useState(false);
  return (
    <div>
      <Seo
        title="Free AI SEO Audit Tool — SEO Auto Engine | OzzieeTech"
        description="Run a free AI-powered SEO audit in seconds: on-page score, keyword opportunities, AI-optimised meta tags and a prioritised fix list for any website."
        path="/seo/auto-engine"
      />
      <EngineHero engine={engine} onOpenAuth={() => setShowAuth(true)} />
      <EngineBody engine={engine} onOpenAuth={() => setShowAuth(true)} />
      <EngineInfoSection />
      {showAuth && !engine.user && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onGoogleLogin={() => engine.startLogin(engine.pendingReportId)}
          onSuccess={() => setShowAuth(false)}
        />
      )}
    </div>
  );
};

export default SeoAutoEngine;
